import {
  React,
  useState,
  useEffect,
  useSelector,
  useDispatch,
} from "../../../globals/imports.js";
import {
  FiZap,
  FiLink,
  FiHash,
  FiEdit3,
  FiCheckCircle,
  FiPlus,
  FiArrowRight,
} from "react-icons/fi";
import UpgradeBanner, { hasGrowth } from "../widgets/UpgradeBanner";
import BuildCommunityStep from "../../Setup/steps/BuildCommunityStep";
import { fetchMyChannels } from "../../../redux/slices/channelItemsSlice";

// Non-Growth view of "Channels AI" — rendered INSIDE the admin shell (sidebar
// stays). A dimmed, wizard-like preview that fills the content area, behind a
// dark overlay with the upgrade banner. Growth users open the real wizard.
const STEPS = [
  { icon: FiLink, title: "Point it at your site", desc: "Paste your website URL and we read it." },
  { icon: FiZap, title: "AI drafts your community", desc: "Root channels + topics, ready to tweak." },
  { icon: FiEdit3, title: "Refine by chatting", desc: "Ask for changes in plain language." },
  { icon: FiCheckCircle, title: "Commit & go live", desc: "One click sets everything up." },
];

const FAUX_CHANNELS = [
  { name: "community", topics: ["introductions", "showcase", "feedback"] },
  { name: "support", topics: ["help", "bugs", "requests"] },
];

const ChannelsAiTab = () => {
  const dispatch = useDispatch();
  const business = useSelector((s) => s.business.business);
  const channels = useSelector((s) => s.channelItems.userChannels) || [];
  // When the community already exists we open a "manage hub" instead of the
  // blank first-time assistant; the assistant only opens on "Add more".
  const [showAssistant, setShowAssistant] = useState(false);

  // Growth (or any plan still in first-time setup) gets the functional AI
  // drafting, in-shell (admin sidebar stays). Only a COMPLETED non-Growth
  // business sees the locked preview.
  const locked =
    !hasGrowth(business) && business?.onboardingState === "complete";
  // "Does a community already exist?" is decided from onboardingState — the
  // business committed a community once it reaches any of these states. We do
  // NOT hinge this on the async /fetch/my/channels list, which can race or come
  // back empty; that list only feeds the channel chips below.
  const committed = [
    "community_committed",
    "complete",
    "embed_live",
  ].includes(business?.onboardingState);
  const hasCommunity = committed || channels.length > 0;

  useEffect(() => {
    dispatch(fetchMyChannels());
  }, [dispatch]);

  if (!locked) {
    // No community yet (true first-timer) OR they chose "Add more" -> the
    // assistant. commit_community is dedupe + cap-safe, so adding more never
    // duplicates existing channels or exceeds the plan limit.
    if (!hasCommunity || showAssistant) {
      return (
        <div className="px-4 py-7 sm:px-6 lg:px-8">
          {/* Add-more (showAssistant) skips the website ask — it was captured
              during the original setup — and drafts immediately. */}
          <BuildCommunityStep
            autoStart={showAssistant}
            onDone={() => {
              // Refresh the count and return to the hub with the new channels.
              dispatch(fetchMyChannels());
              setShowAssistant(false);
            }}
          />
        </div>
      );
    }

    // Community already built -> the manage hub ("continue" experience).
    return (
      <div className="px-4 py-7 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          <div className="relative overflow-hidden rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-6">
            <div className="pointer-events-none absolute -right-10 -top-12 h-36 w-36 rounded-full bg-[#96D7FF] opacity-25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-12 left-8 h-32 w-32 rounded-full bg-[#FBB28B] opacity-25 blur-3xl" />
            <div className="relative">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FBB28B] via-[#96D7FF] to-[#B2EE8F] shadow-md">
                  <FiZap size={20} className="text-[#202020]" />
                </span>
                <div>
                  <h2 className="text-lg font-bold text-theme-secondaryText">
                    Channels AI
                  </h2>
                  <p className="text-xs text-theme-placeholder">
                    {channels.length > 0
                      ? `Your community is set up — ${channels.length} channel${
                          channels.length === 1 ? "" : "s"
                        }.`
                      : "Manage your channels and topics, or add more with AI."}
                  </p>
                </div>
              </div>

              {/* existing channels as chips */}
              {channels.length > 0 && (
                <div className="mb-5 flex flex-wrap gap-2">
                  {channels.slice(0, 12).map((c) => (
                    <span
                      key={c._id}
                      className="inline-flex items-center gap-1 rounded-full border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-1 text-xs font-medium text-theme-secondaryText"
                    >
                      <FiHash size={11} className="text-theme-placeholder" />
                      {c.name}
                    </span>
                  ))}
                  {channels.length > 12 && (
                    <span className="px-2 py-1 text-xs text-theme-placeholder">
                      +{channels.length - 12} more
                    </span>
                  )}
                </div>
              )}

              {/* actions */}
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setShowAssistant(true)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FBB28B] via-[#96D7FF] to-[#B2EE8F] py-3 text-sm font-bold text-[#202020] shadow-md transition-transform hover:-translate-y-0.5"
                >
                  <FiPlus size={16} /> Add more channels with AI
                </button>
                <button
                  type="button"
                  onClick={() => {
                    window.location.hash = "channels";
                  }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-theme-chatDivider py-3 text-sm font-semibold text-theme-secondaryText transition-colors hover:bg-theme-tertiaryBackground"
                >
                  Manage channels <FiArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>

          <p className="mt-3 text-center text-xs text-theme-placeholder">
            Adding more won't touch your existing channels — the assistant only
            creates new ones, up to your plan's limit.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-7 sm:px-6 lg:px-8">
    <div className="relative min-h-[78vh] w-full overflow-hidden rounded-2xl border border-theme-chatDivider bg-theme-primaryBackground">
      {/* Dimmed preview of the setup assistant, filling the area */}
      <div className="pointer-events-none select-none p-6 opacity-60 blur-[1.5px] sm:p-8">
        {/* header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-theme-accent">
            <FiZap size={20} className="text-theme-accentText" />
          </div>
          <div>
            <p className="text-lg font-semibold text-theme-secondaryText">
              Channels AI
            </p>
            <p className="text-sm text-theme-placeholder">
              Let AI set up and refine your community for you.
            </p>
          </div>
        </div>

        {/* how it works */}
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {STEPS.map((s) => (
            <div
              key={s.title}
              className="flex items-start gap-3 rounded-xl border border-theme-chatDivider bg-theme-secondaryBackground p-4"
            >
              <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-theme-accentSoft text-theme-accent">
                <s.icon size={16} />
              </div>
              <div>
                <p className="text-sm font-medium text-theme-secondaryText">
                  {s.title}
                </p>
                <p className="mt-0.5 text-xs text-theme-placeholder">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* url input */}
        <div className="mb-6 flex items-center gap-2 rounded-xl border border-theme-chatDivider bg-theme-tertiaryBackground px-4 py-3">
          <FiHash size={15} className="text-theme-placeholder" />
          <span className="text-sm text-theme-placeholder">
            https://your-site.com
          </span>
        </div>

        {/* faux drafted community */}
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
          A starter community it might draft
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FAUX_CHANNELS.map((c) => (
            <div
              key={c.name}
              className="rounded-xl border border-theme-chatDivider bg-theme-secondaryBackground p-4"
            >
              <p className="mb-2 text-sm font-semibold text-theme-secondaryText">
                #{c.name}
              </p>
              <div className="flex flex-wrap gap-2">
                {c.topics.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-theme-tertiaryBackground px-2.5 py-1 text-xs text-theme-placeholder"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dark overlay + upgrade banner on top */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/55 p-6">
        <div className="w-full max-w-md">
          <UpgradeBanner feature="Channels AI" />
          <p className="mt-3 text-center text-sm text-white/80">
            Re-run the AI setup assistant anytime on the Growth plan.
          </p>
        </div>
      </div>
    </div>
    </div>
  );
};

export default ChannelsAiTab;
